import React from "react";
import "@antv/x6-react-shape";
import TaskStatusesVisualStore from "../stores/TaskStatusesVisualStore";
import { observer } from "mobx-react-lite";
import {
    Flowchart,
    IFlowchartGraph,
    XFlowGraphCommands,
} from "@ant-design/flowchart/lib";
import { FlowEditorSelection, TaskStatusTransitionVertex } from "../types";
import { FormInstance, Radio } from "antd";
import { Edge, Node } from "@antv/x6";
import StandardWorkflowNotice from "./StandardWorkflowNotice";
import { FlowMode } from "./TaskStatusFlowEditor.types";
import {
    createAllowedActionsSnapshot,
    convertStatusToNode,
    convertTransitionToEdge,
    createWorkflowSnapshot,
    normalizeTransitionVertices,
} from "./taskStatusFlowGraph";
import TaskStatusFlowPropertiesPanel from "./TaskStatusFlowPropertiesPanel";
import TaskStatusFlowScaleToolbar from "./TaskStatusFlowScaleToolbar";
type Props = {
    areActionsEnabled: boolean;
    form: FormInstance;
    isCustomMode: boolean;
    onCreateCustomTaskType: () => void;
    onActionsChange?: (
        actions: {
            saveFlow: () => Promise<void>;
            cancelFlowChanges: () => Promise<void>;
            hasUnsavedChanges: boolean;
        } | null,
    ) => void;
    store: TaskStatusesVisualStore;
};

const TaskStatusFlowEditor: React.FC<Props> = ({
    areActionsEnabled,
    form,
    isCustomMode,
    onActionsChange,
    onCreateCustomTaskType,
    store,
}) => {
    const [graph, setGraph] = React.useState<IFlowchartGraph>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [flowchartApp, setFlowchartApp] = React.useState<any>();
    const [selectedItem, setSelectedItem] = React.useState<FlowEditorSelection>(
        { type: "none" },
    );
    const [flowMode, setFlowMode] = React.useState<FlowMode>("parent");
    const [initialWorkflowSnapshot, setInitialWorkflowSnapshot] =
        React.useState(() => createWorkflowSnapshot(store.taskStatuses));
    const [initialAllowedActionsSnapshot, setInitialAllowedActionsSnapshot] =
        React.useState(() => createAllowedActionsSnapshot(store.taskStatuses));

    const syncEdgeRoute = React.useCallback(
        (edge: Edge) => {
            const sourceStatusId = edge.getSourceNode()?.id;
            const targetStatusId = edge.getTargetNode()?.id;
            const sourcePort = edge.getSourcePortId();
            const targetPort = edge.getTargetPortId();

            if (
                !sourceStatusId ||
                !targetStatusId ||
                !sourcePort ||
                !targetPort
            ) {
                return;
            }

            const sourceStatus = store.taskStatuses.find(
                (status) => status.id === sourceStatusId,
            );
            const transition = sourceStatus?.transitions?.find(
                (candidate) =>
                    candidate.targetStatusId === targetStatusId &&
                    candidate.sourcePort === sourcePort &&
                    candidate.targetPort === targetPort,
            );

            if (transition) {
                transition.vertices = normalizeTransitionVertices(
                    edge.getVertices() as TaskStatusTransitionVertex[],
                );
            }
        },
        [store],
    );

    const updateEdgeTools = React.useCallback(
        (selection: unknown[]) => {
            if (!graph) {
                return;
            }

            graph.getEdges().forEach((edge) => edge.removeTools());

            if (!isCustomMode || selection.length === 0) {
                return;
            }

            const selectedEdge = selection[0];
            if (selectedEdge instanceof Edge) {
                selectedEdge.addTools([
                    {
                        name: "vertices",
                        args: {
                            snapRadius: 16,
                            removeRedundancies: true,
                        },
                    },
                ]);
            }
        },
        [graph, isCustomMode],
    );

    React.useEffect(() => {
        reloadGraph();
    }, [graph, store.taskStatuses, flowMode]);

    React.useEffect(() => {
        if (graph && flowchartApp) {
            graph.toggleMultipleSelection(false);
            const handleGraphSelectionChanged = (args: {
                selected: unknown[];
            }) => {
                handleSelectionChanged(args.selected);
                updateEdgeTools(args.selected);
            };
            const handleEdgeAdded = (args: { edge: Edge }) => {
                args.edge.setAttrs({ line: { strokeDasharray: "" } });
            };
            const handleEdgeConnected = (args: {
                edge: Edge;
                currentPort?: string;
            }) => {
                const sourceStatus = store.taskStatuses.find(
                    (s) => s.id === args.edge.getSourceNode()!.id,
                );
                if (sourceStatus) {
                    if (!sourceStatus.transitions) {
                        sourceStatus.transitions = [];
                    }

                    sourceStatus.transitions.push({
                        sourcePort: args.edge.getSourcePortId()!,
                        targetPort: args.currentPort!,
                        targetStatusId: args.edge.getTargetNode()!.id,
                        vertices: normalizeTransitionVertices(
                            args.edge.getVertices() as TaskStatusTransitionVertex[],
                        ),
                    });
                }
            };
            const handleEdgeVerticesChanged = (args: { edge: Edge }) => {
                syncEdgeRoute(args.edge);
            };
            const handleNodePositionChanged = (args: {
                node: Node;
                current: { x: number; y: number };
            }) => {
                const statusDesign = store.taskStatuses.find(
                    (s) => s.id === args.node.id,
                )?.design;
                if (statusDesign) {
                    Object.assign(statusDesign, args.current);
                }
            };
            const handleNodeSizeChanged = (args: {
                node: Node;
                current: { width: number; height: number };
            }) => {
                const statusDesign = store.taskStatuses.find(
                    (s) => s.id === args.node.id,
                )?.design;
                if (statusDesign) {
                    Object.assign(statusDesign, args.current);
                }
            };

            graph.on("selection:changed", handleGraphSelectionChanged);
            graph.on("edge:added", handleEdgeAdded);
            graph.on("edge:connected", handleEdgeConnected);
            graph.on("edge:change:vertices", handleEdgeVerticesChanged);
            graph.on("node:change:position", handleNodePositionChanged);
            graph.on("node:change:size", handleNodeSizeChanged);

            return () => {
                graph.off("selection:changed", handleGraphSelectionChanged);
                graph.off("edge:added", handleEdgeAdded);
                graph.off("edge:connected", handleEdgeConnected);
                graph.off("edge:change:vertices", handleEdgeVerticesChanged);
                graph.off("node:change:position", handleNodePositionChanged);
                graph.off("node:change:size", handleNodeSizeChanged);
            };
        }

        return undefined;
    }, [flowchartApp, graph, store, syncEdgeRoute, updateEdgeTools]);

    React.useEffect(() => {
        resetForm();
    }, [form, selectedItem, flowMode]);

    React.useEffect(() => {
        setFlowMode("parent");
    }, [store.selectedTaskType]);

    React.useEffect(() => {
        if (!store.taskType?.distinctSubtaskStatuses && flowMode === "child") {
            setFlowMode("parent");
        }
    }, [flowMode, store.taskType?.distinctSubtaskStatuses]);

    React.useEffect(() => {
        setInitialWorkflowSnapshot(createWorkflowSnapshot(store.taskStatuses));
        setInitialAllowedActionsSnapshot(
            createAllowedActionsSnapshot(store.taskStatuses),
        );
    }, [store.selectedTaskType, store.taskStatuses]);

    const workflowSnapshot = createWorkflowSnapshot(store.taskStatuses);
    const allowedActionsSnapshot = createAllowedActionsSnapshot(
        store.taskStatuses,
    );
    const hasWorkflowChanges =
        isCustomMode && workflowSnapshot !== initialWorkflowSnapshot;
    const hasAllowedActionChanges =
        allowedActionsSnapshot !== initialAllowedActionsSnapshot;
    const hasUnsavedChanges = hasWorkflowChanges || hasAllowedActionChanges;

    const reloadGraph = React.useCallback(() => {
        if (graph) {
            graph.clearCells();
            resetForm();

            const nodes =
                store.taskStatuses
                    .filter((x) => x.isSubtaskStatus === (flowMode === "child"))
                    .map((s, i) => convertStatusToNode(s, i)) || [];
            const edges =
                store.taskStatuses
                    .filter(
                        (s) =>
                            !!s.transitions &&
                            s.isSubtaskStatus === (flowMode === "child"),
                    )
                    .reduce(
                        (trs, tr) => [
                            ...trs,
                            ...tr.transitions!.map((t) => ({
                                sourceStatusId: tr.id,
                                ...t,
                            })),
                        ],
                        [],
                    )
                    .map((e) => convertTransitionToEdge(e)) || [];

            graph.addNodes(nodes);
            graph.addEdges(edges);

            if (nodes.length > 0) {
                graph.zoomToFit({
                    padding: 32,
                    maxScale: 1,
                });
                graph.centerContent();
            }
        }
    }, [graph, store.taskStatuses, flowMode]);

    const resetForm = () => {
        form.resetFields();

        if (selectedItem.item) {
            if (selectedItem.item.isEdge()) {
                form.setFieldsValue({
                    source: store.taskStatuses.find(
                        (x) => x.id === selectedItem.item.source.cell,
                    )?.name,
                    target: store.taskStatuses.find(
                        (x) => x.id === selectedItem.item.target.cell,
                    )?.name,
                });
            } else if (selectedItem.item.isNode()) {
                form.setFieldsValue(
                    store.taskStatuses.find(
                        (x) => x.id === selectedItem.item!.id,
                    ),
                );
            }
        } else if (selectedItem) {
            form.setFieldsValue({
                initialStatus:
                    store.taskStatuses.find(
                        (x) =>
                            x.isDefault === true &&
                            x.isSubtaskStatus === (flowMode === "child"),
                    )?.id ?? undefined,
                finalStatus:
                    store.taskStatuses.find(
                        (x) =>
                            x.isFinal === true &&
                            x.isSubtaskStatus === (flowMode === "child"),
                    )?.id ?? undefined,
            });
        }
    };

    const saveGraph = (data: unknown) => {
        console.log("Graph:", data);
    };

    const printGraph = React.useCallback(() => {
        flowchartApp
            ?.getGraphData()
            .then((v: unknown) => console.log("Graph data:", v));
    }, [flowchartApp]);

    const saveFlow = React.useCallback(async () => {
        await form.validateFields();
        if (hasWorkflowChanges) {
            await store.submitNewStatusDependencies();
        } else if (hasAllowedActionChanges) {
            await store.submitStatusActions();
        }
    }, [form, hasAllowedActionChanges, hasWorkflowChanges, store]);

    const cancelFlowChanges = React.useCallback(async () => {
        if (!store.selectedTaskType) {
            return;
        }

        setSelectedItem({ type: "none" });
        await store.loadTaskStatuses(store.selectedTaskType);
    }, [store]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSelectionChanged = (selection: any[]) => {
        if (selection?.length) {
            if (selection[0] instanceof Edge) {
                flowchartApp
                    ?.getEdgeById(selection[0].id)
                    .then((e: unknown) =>
                        setSelectedItem({ type: "edge", item: e }),
                    );
            } else if (selection[0] instanceof Node) {
                flowchartApp
                    ?.getNodeById(selection[0].id)
                    .then((n: unknown) =>
                        setSelectedItem({ type: "node", item: n }),
                    );
            }
        } else {
            setSelectedItem({ type: "none" });
        }
    };

    const handleZoom = (factor: number | string) => {
        flowchartApp?.executeCommand(XFlowGraphCommands.GRAPH_ZOOM.id, {
            factor: factor,
            zoomOptions:
                typeof factor === "string"
                    ? undefined
                    : {
                          absolute: false,
                          minScale: 0.1,
                          maxScale: 3.0,
                      },
        });
    };

    React.useEffect(() => {
        if (!onActionsChange) {
            return;
        }

        onActionsChange({
            saveFlow,
            cancelFlowChanges,
            hasUnsavedChanges,
        });

        return () => onActionsChange(null);
    }, [cancelFlowChanges, hasUnsavedChanges, onActionsChange, saveFlow]);

    return (
        <div className="flow-tab-content">
            {!isCustomMode && (
                <StandardWorkflowNotice
                    actionLink={
                        store.taskType?.isSystemDefault
                            ? {
                                  disabled: !areActionsEnabled,
                                  label: "Create custom task type",
                                  onClick: onCreateCustomTaskType,
                              }
                            : undefined
                    }
                    isSystemDefault={!!store.taskType?.isSystemDefault}
                    containerClassName="flow-tab-standard-workflow-notice"
                />
            )}
            {store.taskType?.distinctSubtaskStatuses && (
                <Radio.Group
                    className="flow-type-toggle"
                    buttonStyle="solid"
                    value={flowMode}
                    onChange={(v) => setFlowMode(v.target.value)}
                >
                    <Radio.Button value="parent">Main tasks</Radio.Button>
                    <Radio.Button value="child">Subtasks</Radio.Button>
                </Radio.Group>
            )}

            <div className="flow-editor-container">
                <Flowchart
                    isAutoCenter
                    toolbarPanelProps={{
                        position: { top: 0, left: 0, right: 0 },
                        show: false,
                    }}
                    scaleToolbarPanelProps={{
                        position: { bottom: 8, right: 8 },
                        show: false,
                    }}
                    nodePanelProps={{
                        position: { width: 250, top: 40, bottom: 0, left: 0 },
                        show: false,
                    }}
                    canvasProps={{
                        position: { top: 0, left: 0, right: 300, bottom: 0 },
                        config: {
                            connecting: {
                                allowMulti: false,
                            },
                        },
                    }}
                    detailPanelProps={{
                        position: { width: 250, top: 40, bottom: 0, right: 0 },
                        show: false,
                    }}
                    keyBindingProps={false}
                    contextMenuPanelProps={{ show: false }}
                    onSave={saveGraph}
                    onReady={(g, app) => {
                        setGraph(g);
                        setFlowchartApp(app);
                    }}
                />
                <TaskStatusFlowPropertiesPanel
                    store={store}
                    form={form}
                    graph={graph}
                    selectedItem={selectedItem}
                    flowMode={flowMode}
                    isCustomMode={isCustomMode}
                    onReloadGraph={reloadGraph}
                />
                <TaskStatusFlowScaleToolbar
                    canInteract={areActionsEnabled}
                    onPrintGraph={printGraph}
                    onReloadGraph={reloadGraph}
                    onZoom={handleZoom}
                />
            </div>
        </div>
    );
};

export default observer(TaskStatusFlowEditor);
